import { Area, AreaChart, ResponsiveContainer } from "recharts";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/shared/lib/utils";

export interface MetricKpiCardProps {
  label: string;
  value: string | number;
  hint?: string;
  /** Serie numérica para sparkline (orden cronológico) */
  sparkline?: number[];
  className?: string;
  accentClassName?: string;
}

/**
 * KPI del hub de métricas con sparkline opcional.
 */
export function MetricKpiCard({
  label,
  value,
  hint,
  sparkline,
  className,
  accentClassName,
}: MetricKpiCardProps): React.JSX.Element {
  const chartData =
    sparkline?.map((v, i) => ({
      i,
      v,
    })) ?? [];
  const gradientId = `spark-${label.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-end justify-between gap-2">
          <p
            className={cn(
              "text-3xl font-semibold tracking-tight tabular-nums text-gray-900 dark:text-gray-100",
              accentClassName
            )}
          >
            {value}
          </p>
          {chartData.length > 1 && (
            <div className="h-10 w-24 shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke="var(--chart-1)"
                    strokeWidth={1.5}
                    fill={`url(#${gradientId})`}
                    isAnimationActive={false}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}
